import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'

import { combineDateAndTime } from './volunteeringActivitySubmitHelpers'
import { TimePickerHelpers } from '@percent/domain/giving'
import { useSharedValidationRules } from '@percent/workplace-giving/common/hooks/useSharedValidationRules/useSharedValidationRules'
import { VolunteeringTimeLogType } from '@percent/workplace-giving/api/opportunity/dto'
import { usePostHogFeatureFlag } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'

type GetValidationSchemaOptions = {
  mode: 'create' | 'edit'
  platform: VolunteeringTimeLogType
  requireOrgFieldsInForms: boolean | null
  volunteeringWorkingHoursCheck?: boolean | null
}

const to24h = (time: string) => TimePickerHelpers.formatTime(time, false)

export function useVolunteeringActivityValidationSchema() {
  const { t } = useTranslation()
  const { validateString, validateDate } = useSharedValidationRules()
  const noFutureTimeLogs = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.WPG_NO_FUTURE_TIME_LOGS)

  return ({ mode, platform, requireOrgFieldsInForms, volunteeringWorkingHoursCheck }: GetValidationSchemaOptions) =>
    Yup.object().shape({
      timeLogs: Yup.array()
        .of(
          Yup.object()
            .shape({
              date: validateDate(),
              startTime: Yup.string().required(t('workplace_giving.validation.requiredField')),
              endTime: Yup.string().required(t('workplace_giving.validation.requiredField'))
            })
            .test('endTime-after-startTime', t('workplace_giving.validation.endTimeAfterStartTime'), (value, ctx) => {
              if (!value?.startTime || !value?.endTime) return true
              const start = to24h(value.startTime)
              const end = to24h(value.endTime)

              if (end <= start) {
                return ctx.createError({
                  path: `${ctx.path}.endTime`,
                  message: t('workplace_giving.validation.endTimeAfterStartTime')
                })
              }

              return true
            })
            .test('date-not-in-future', t('workplace_giving.validation.endTimeNotInFuture'), (value, ctx) => {
              if (!noFutureTimeLogs) return true

              if (!value?.date) return true

              const today = new Date()
              today.setHours(0, 0, 0, 0)

              const selected = new Date(value.date)
              selected.setHours(0, 0, 0, 0)

              if (selected.getTime() > today.getTime()) {
                return ctx.createError({
                  path: `${ctx.path}.date`,
                  message: t('workplace_giving.validation.endTimeNotInFuture')
                })
              }

              return true
            })
            .test('endTime-not-in-future', t('workplace_giving.validation.endTimeNotInFuture'), (value, ctx) => {
              if (!noFutureTimeLogs) return true

              if (!value?.date || !value?.endTime) return true

              const combinedEnd = combineDateAndTime(value.date, value.endTime)

              if (combinedEnd.getTime() > Date.now()) {
                return ctx.createError({
                  path: `${ctx.path}.endTime`,
                  message: t('workplace_giving.validation.endTimeNotInFuture')
                })
              }

              return true
            })
        )
        .min(1, t('workplace_giving.validation.requiredField')),
      platform: Yup.string().oneOf(Object.values(VolunteeringTimeLogType)),
      opportunityId: validateString({ optional: platform === VolunteeringTimeLogType.OffPlatform }),
      organisationCountry: validateString({ optional: !requireOrgFieldsInForms }),
      organisationId: validateString({ optional: !requireOrgFieldsInForms }),
      activities: Yup.array()
        .of(Yup.string())
        .when(['platform'], {
          is: VolunteeringTimeLogType.OffPlatform,
          then: schema =>
            schema
              .min(1, t('workplace_giving.validation.requiredField'))
              .required(t('workplace_giving.validation.requiredField'))
        }),
      skills: Yup.array().of(Yup.string()).optional(),
      duringWorkingHours:
        mode === 'edit'
          ? Yup.boolean().nullable()
          : Yup.boolean().when([], {
              is: () => volunteeringWorkingHoursCheck === true,
              then: schema =>
                schema.test('is-defined', t('workplace_giving.validation.requiredField'), value => value !== undefined)
            })
    })
}
